/**
 * WebAuthn utility functions for the Web Login client.
 * Provides base64url encoding/decoding and error handling for WebAuthn operations.
 */

/**
 * WebAuthn error types
 */
export enum WebAuthnErrorType {
  NOT_SUPPORTED = 'NOT_SUPPORTED',
  USER_CANCELLED = 'USER_CANCELLED',
  TIMEOUT = 'TIMEOUT',
  NOT_FOUND = 'NOT_FOUND',
  INVALID_STATE = 'INVALID_STATE',
  SECURITY_ERROR = 'SECURITY_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * WebAuthn error with friendly message
 */
export interface WebAuthnError {
  type: WebAuthnErrorType;
  message: string;
  originalError?: Error;
}

/**
 * Helper to check if WebAuthn is supported in the current browser
 */
export const isWebAuthnSupported = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    window.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential === 'function'
  );
};

/**
 * Helper to convert base64url string to Uint8Array
 *
 * @param base64url - Base64url encoded string
 * @returns Uint8Array buffer
 */
export const base64urlToUint8Array = (base64url: string): Uint8Array => {
  // Convert base64url to base64 (add padding and replace characters)
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;

  // Decode base64 to binary string, then to Uint8Array
  const binaryString = window.atob(base64);

  return Uint8Array.from(binaryString, char => char.charCodeAt(0));
};

/**
 * Helper to convert ArrayBuffer to base64url string
 *
 * @param buffer - ArrayBuffer to encode
 * @returns Base64url encoded string
 */
export const arrayBufferToBase64url = (buffer: ArrayBuffer): string => {
  // Convert ArrayBuffer to binary string, then encode to base64url
  const binaryString = String.fromCharCode(...new Uint8Array(buffer));

  return window
    .btoa(binaryString)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

/**
 * Normalize server-sent transports for a PublicKeyCredentialDescriptor.
 *
 * The descriptor member is optional but NOT nullable — handing the browser a
 * `null` throws a TypeError, and a credential may have no stored transports.
 * Values are relayed unfiltered: the WebAuthn registry is extensible and the
 * spec warns that removing a reported transport can prevent use of the
 * credential. The cast accounts for lib.dom typing this union more narrowly
 * than the WebAuthn spec.
 *
 * @param transports - Transports from the server, possibly null or empty
 * @returns The reported transports, or undefined to omit the member
 */
export const toAuthenticatorTransports = (
  transports: string[] | null | undefined
) =>
  transports?.length ? (transports as AuthenticatorTransport[]) : undefined;

/**
 * Parse WebAuthn error to provide user-friendly messages
 *
 * @param error - The error thrown by WebAuthn API
 * @returns Structured error with user-friendly message
 */
export const parseWebAuthnError = (error: unknown): WebAuthnError => {
  if (!(error instanceof Error)) {
    return {
      type: WebAuthnErrorType.UNKNOWN_ERROR,
      message: 'An unknown error occurred during authentication',
    };
  }

  const errorName = error.name.toLowerCase();
  const errorMessage = error.message.toLowerCase();

  // User cancelled or aborted
  if (
    errorName.includes('abort') ||
    errorName.includes('notallowed') ||
    errorMessage.includes('cancel')
  ) {
    return {
      type: WebAuthnErrorType.USER_CANCELLED,
      message: 'Authentication was cancelled. Please try again.',
      originalError: error,
    };
  }

  // Timeout
  if (errorName.includes('timeout') || errorMessage.includes('timeout')) {
    return {
      type: WebAuthnErrorType.TIMEOUT,
      message: 'Authentication timed out. Please try again.',
      originalError: error,
    };
  }

  // No matching credentials found
  if (errorName.includes('notfound')) {
    return {
      type: WebAuthnErrorType.NOT_FOUND,
      message:
        'No matching authenticator found. Please use a registered passkey or biometric.',
      originalError: error,
    };
  }

  // Not supported
  if (errorName.includes('notsupported')) {
    return {
      type: WebAuthnErrorType.NOT_SUPPORTED,
      message:
        'WebAuthn is not supported in your browser. Please use a modern browser.',
      originalError: error,
    };
  }

  // Invalid state
  if (errorName.includes('invalidstate')) {
    return {
      type: WebAuthnErrorType.INVALID_STATE,
      message:
        'Authentication is in an invalid state. Please refresh and try again.',
      originalError: error,
    };
  }

  // Security error (not secure context)
  if (errorName.includes('security')) {
    return {
      type: WebAuthnErrorType.SECURITY_ERROR,
      message:
        'WebAuthn requires a secure connection (HTTPS). Please check your connection.',
      originalError: error,
    };
  }

  // Network error
  if (errorName.includes('network')) {
    return {
      type: WebAuthnErrorType.NETWORK_ERROR,
      message: 'Network error. Please check your connection and try again.',
      originalError: error,
    };
  }

  // Unknown error
  return {
    type: WebAuthnErrorType.UNKNOWN_ERROR,
    message:
      error.message ||
      'An error occurred during authentication. Please try again.',
    originalError: error,
  };
};
